import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { dispatch } from 'redux/store';
import { rejectWithError } from '../dispatchError';
import {
  ReviewCategory,
  ReviewListCategory,
  ReviewResponseStatus,
  ReviewsState,
  UpdateReviewResponseRequest
} from '../../@types/reviews';
import { translator } from 'translation/useTranslator';

const reviewResponseStatuses: Record<ReviewListCategory, ReviewResponseStatus> = {
  [ReviewCategory.consent]: 'consented',
  [ReviewCategory.public]: 'public',
  [ReviewCategory.flagged]: 'flagged'
};

const initialState: ReviewsState = {
  isLoading: false,
  consentReviews: [],
  publicReviews: [],
  flaggedReviews: [],
  total: 0,
  skip: 0,
  take: 10
};

const slice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    setConsentReviews(state, action) {
      state.consentReviews = action.payload;
    },
    setPublicReviews(state, action) {
      state.publicReviews = action.payload;
    },
    setFlaggedReviews(state, action) {
      state.flaggedReviews = action.payload;
    },
    setPagination(state, action) {
      state.total = action.payload.total || initialState.total;
      state.skip = action.payload.skip || initialState.skip;
      state.take = action.payload.take || initialState.take;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { setPagination } = slice.actions;

export const getReviews = ({
  reviewType,
  take,
  skip
}: {
  reviewType: ReviewListCategory;
  take?: number;
  skip?: number;
}) => {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const params = {
        skip: skip ?? initialState.skip,
        take: take ?? initialState.take,
        status: reviewResponseStatuses[reviewType]
      };
      const response = await axios.get(`/review-responses`, { params });
      const reviews = response.data?.results ?? [];
      switch (reviewType) {
        case ReviewCategory.consent:
          dispatch(slice.actions.setConsentReviews(reviews));
          break;
        case ReviewCategory.public:
          dispatch(slice.actions.setPublicReviews(reviews));
          break;
        case ReviewCategory.flagged:
          dispatch(slice.actions.setFlaggedReviews(reviews));
          break;
      }

      dispatch(slice.actions.setPagination(response.data));

      dispatch(slice.actions.stopLoading());

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.reviewsFetched() ?? 'Reviews fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchReviews() ?? 'Could not fetch reviews'
      });
    }
  };
};

export const changeReviewType = ({
  currentReviewType,
  id,
  isFlagged,
  isPublic
}: {
  currentReviewType: ReviewListCategory;
  id: number;
  isFlagged?: boolean;
  isPublic?: boolean;
}) => {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const request: UpdateReviewResponseRequest = {
        isPublic: isPublic ?? false,
        isFlagged: isFlagged ?? false
      };
      await axios.put(`/review-responses/${id}`, request);

      //get new reviews
      dispatch(
        getReviews({
          reviewType: currentReviewType
        })
      );

      dispatch(slice.actions.stopLoading());
      let movedToType = 'responses';
      if (isFlagged) {
        movedToType = 'flagged';
      }
      if (isPublic) {
        movedToType = 'public';
      }

      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.responseMovedTo(movedToType) ?? `Response moved to "${movedToType}"`
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotUpdateReview() ?? 'Could not update review'
      });
    }
  };
};
